import React, { useState, useCallback, useMemo, useRef } from 'react';
import {Box,Grid, TextField, Typography, Button, Container, Slider, Radio, RadioGroup, FormControlLabel, FormControl,
  FormLabel, Alert} from '@mui/material';
import { Add, Info} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify'

export default function CreateCouponDialog() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [formData, setFormData] = useState({
    offerTitle: '',
    couponValue: '',
    minAmount: '',
    maxAmount: '',
    shortDesc: '',
    longDesc: '',
    dateStart: '',
    dateEnd: '',
    valueType: 'fixed', // fixed or percentage
  });

  const [errors, setErrors] = useState({});

  const shortDescMaxLength = 150;
  const longDescMaxLength = 500;

  // Memoized slider marks
  const marks = useMemo(() => [
    { value: 5, label: '5₹' }, { value: 10, label: '10₹' },
    { value: 15, label: '15₹' }, { value: 20, label: '20₹' },
    { value: 25, label: '25₹' }, { value: 30, label: '30₹' },
  ], []);

  const marks1 = useMemo(() => [
    { value: 15, label: '15₹' }, { value: 20, label: '20₹' },
    { value: 25, label: '25₹' }, { value: 30, label: '30₹' },
    { value: 35, label: '35₹' }, { value: 40, label: '40₹' },
    { value: 45, label: '45₹' }, { value: 50, label: '50₹' },
  ], []);

  // Handlers
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  }, []);

  const handleSubmit = () => {
    const tempErrors = {};
    if (!formData.offerTitle.trim()) tempErrors.offerTitle = 'Offer title is required';
    if (!formData.dateStart) tempErrors.dateStart = 'Active date is required';
    if (!formData.dateEnd) tempErrors.dateEnd = 'Expiry date is required';
    if (!formData.shortDesc.trim()) tempErrors.shortDesc = 'Short description is required';
    if (formData.shortDesc.length > shortDescMaxLength) tempErrors.shortDesc = `Short description cannot exceed ${shortDescMaxLength} characters`;
    if (!formData.longDesc.trim()) tempErrors.longDesc = 'Long description is required';
    if (formData.longDesc.length > longDescMaxLength) tempErrors.longDesc = `Long description cannot exceed ${longDescMaxLength} characters`;

    setErrors(tempErrors);

    if (Object.keys(tempErrors).length === 0) {
      
         toast.success('Coupon is created sucessfully')
       
      console.log('Form submitted:', formData);
      navigate('/dashboard');
    }
  };

  return (
    <Container sx={{ my: 6 }}>
       <ToastContainer />
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          background: 'linear-gradient(195deg, #49a3f1, #1A73E8)',
          p: 1,
          color: '#fff',
          borderRadius: '4px',
          mb: 3
        }}
      >
        <Add /> Create Coupon
      </Typography>

      <Grid container spacing={2}>
        {/* Coupon Title */}
        <Grid item  size={{ xs: 12,sm:12, md: 12,  }}>
          <TextField
            fullWidth
            label="Coupon Title"
            value={formData.offerTitle}
            onChange={(e) => updateField('offerTitle', e.target.value)}
            autoFocus
            error={!!errors.offerTitle}
            helperText={errors.offerTitle}
          />
        </Grid>

        {/* Min & Max Amount */}
        <Grid item  size={{ xs: 12,sm:12, md: 6,  }} sx={{ display: 'flex' }}>
          <TextField
            fullWidth
            label="Min Order Amount"
            type="number"
            value={formData.minAmount}
            onChange={(e) => updateField('minAmount', e.target.value)}
          />
          <Info sx={{ fontSize: 20, ml: 1, alignSelf: 'center' }} />
        </Grid>
        <Grid item  size={{ xs: 12,sm:12, md: 6,  }} sx={{ display: 'flex' }}>
          <TextField
            fullWidth
            label="Max Order Amount"
            type="number"
            value={formData.maxAmount}
            onChange={(e) => updateField('maxAmount', e.target.value)}
          />
          <Info sx={{ fontSize: 20, ml: 1, alignSelf: 'center' }} />
        </Grid>

        {/* Value Type + Value */}
        <Grid item  size={{ xs: 12,sm:12, md: 12,  }}>
          <FormControl>
            <FormLabel>Coupon Value Type</FormLabel>
            <RadioGroup
              row
              value={formData.valueType}
              onChange={(e) => updateField('valueType', e.target.value)}
            >
              <FormControlLabel value="fixed" control={<Radio />} label="Fixed" />
              <FormControlLabel value="percentage" control={<Radio />} label="Percentage" />
            </RadioGroup>
          </FormControl>
          <TextField
            fullWidth
            label="Coupon Value"
            value={formData.couponValue}
            onChange={(e) => updateField('couponValue', e.target.value)}
            error={!!errors.couponValue}
            helperText={errors.couponValue}
          />
        </Grid>

        {/* Short Description */}
        <Grid item  size={{ xs: 12,sm:12, md: 12,  }}>
          <TextField
            fullWidth
            label="Short Description"
            value={formData.shortDesc}
            onChange={(e) => updateField('shortDesc', e.target.value)}
            error={!!errors.shortDesc}
            helperText={errors.shortDesc}
          />
        </Grid>

        {/* Long Description */}
        <Grid item  size={{ xs: 12,sm:12, md: 12,  }}>
          <TextField
            fullWidth
            label="Long Description"
            multiline
            minRows={5}
            value={formData.longDesc}
            onChange={(e) => updateField('longDesc', e.target.value)}
            error={!!errors.longDesc}
            helperText={errors.longDesc}
          />
        </Grid>

        {/* Sliders */}
        <Grid item  size={{ xs: 12,sm:12, md: 6,  }} sx={{px:1}}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Distribution Bid <Info sx={{ fontSize: 20 }} />
          </Typography>
          <Slider
            min={5}
            max={30}
            defaultValue={5}
            step={1}
            marks={marks}
            valueLabelDisplay="auto"
          />
        </Grid>
        <Grid item  size={{ xs: 12,sm:12, md: 6,  }} sx={{px:1}}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Redemption Bid <Info sx={{ fontSize: 20 }} />
          </Typography>
          <Slider
            min={15}
            max={50}
            defaultValue={15}
            step={1}
            marks={marks1}
            valueLabelDisplay="auto"
          />
        </Grid>

        {/* Dates */}
        <Grid item  size={{ xs: 12,sm:12, md: 6,  }}>
          <TextField
            fullWidth
            type="date"
            label="Active Date"
            InputLabelProps={{ shrink: true }}
            value={formData.dateStart}
            onChange={(e) => updateField('dateStart', e.target.value)}
            error={!!errors.dateStart}
            helperText={errors.dateStart}
            inputRef={inputRef}
             onClick={() => {
            if (inputRef.current && inputRef.current.showPicker) {
              inputRef.current.showPicker(); // Triggers calendar
            }
      }}
          />
        </Grid>
        <Grid item  size={{ xs: 12,sm:12, md: 6,  }}>
          <TextField
            fullWidth
            type="date"
            label="Expiry Date"
            InputLabelProps={{ shrink: true }}
            value={formData.dateEnd}
            onChange={(e) => updateField('dateEnd', e.target.value)}
            error={!!errors.dateEnd}
            helperText={errors.dateEnd}
             inputRef={inputRef}
             onClick={() => {
            if (inputRef.current && inputRef.current.showPicker) {
              inputRef.current.showPicker();
            }}}
          />
        </Grid>
      </Grid>

      {/* Buttons */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="contained"
          sx={{ backgroundColor: '#000', color: '#fff' }}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <Button
          variant="contained"
          sx={{ mx: 1 }}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Box>
    </Container>
  );
}
